import * as z from "zod";

import type { StudioEntitlement as RestStudioEntitlement } from "@mountaya/client";

import type { ToolFailure, ViewerPreferences, ViewerSeam } from "../../bus/rules/types.js";
import { notifyListeners } from "../../rules/listeners.js";

/**
 * studioEntitlements is every entitlement of the contract, each keyed by itself, in the order the
 * schema lists them.
 *
 * The `satisfies` clause ties it to the generated `StudioEntitlement` in both directions: a
 * feature the contract adds leaves a missing key, and one it does not declare is an excess
 * property, so `tsc` fails at this declaration either way. The studio gates on these names and
 * the platform answers them, so a name only one side spells is a gate that never opens.
 */
const studioEntitlements = {
  "context-menu-insights": "context-menu-insights",
  copilot: "copilot",
  embed: "embed",
  "map-overlays": "map-overlays",
  "map-pois": "map-pois",
  "profile-insights": "profile-insights",
  publish: "publish",
  "video-export": "video-export",
  "video-organization-modes": "video-organization-modes",
  "white-label": "white-label",
} as const satisfies { [K in RestStudioEntitlement]: K };

/**
 * studioEntitlementSchema is every entitlement the map studio reads off a viewer, and each one
 * names a FEATURE rather than a tier.
 *
 * It is the contract's own vocabulary, `StudioEntitlement` of `@mountaya/client`. Mountaya answers
 * which of these a person holds in each scope: on every membership `getMyOrganizations` returns
 * (`Membership.studio_entitlements`), resolved from what the organization's agreement
 * includes and from the person's role. Read with a publishable key, that answer is narrowed by the
 * organization's safety rules, as what the key is served is: `profile-insights` only where
 * `with_profile_analysis` is set, `map-pois` only where the overlay allowlist (`with_overlays`)
 * admits the points of interest, and `map-overlays` only where it admits another overlay. A
 * licensee whose studio draws no overlays checks those rules, not its agreement. A viewer reads
 * that answer ({@link createPlatformViewer}) and decides none of it. A feature rather than a tier
 * is what lets an agreement that covers the co-pilot and not video export be stated exactly.
 *
 * The names are kebab case, as every member of the contract's vocabularies is: the surface first
 * where a feature belongs to one (`video-export`), and the surface alone where the feature is
 * the surface (`copilot`).
 *
 * What each one unlocks:
 *
 * - `context-menu-insights`: the map context menu's external place link.
 *   Reachability from a point is not among them: it creates a zone, which the scope's own write
 *   rule answers (`StudioScopeSeam.writeRefusal`) rather than an entitlement.
 * - `copilot`: the co-pilot: its tab strip, its conversations, its runs.
 * - `embed`: embedding an itinerary or a collection in a third-party site.
 * - `map-overlays`: the overlays and the enrichments drawn over the base map, the points of
 *   interest aside, with the timestamp scrubber. It is the one gate for an overlay: the layers
 *   picker offers one, the map draws it and the sample tools sample it only while the viewer
 *   holds it, so a viewer without it can neither see, select nor sample one. Mountaya answers it
 *   in the scope of an organization whose agreement includes Map Studio, so it follows the scope
 *   the studio is in, and under a publishable key only where the organization's overlay allowlist
 *   admits an overlay beyond the points of interest. An embed or a share link draws the overlays
 *   of the organization that published it, whoever reads it, and its viewer states that grant
 *   itself ({@link createStaticViewer}).
 * - `map-pois`: the points of interest, the one enrichment `map-overlays` does not gate: the POI
 *   groups, the POI overlay menu and a waypoint picked from a point of interest. Under a
 *   publishable key it is answered only where the overlay allowlist admits the points of interest.
 * - `profile-insights`: the elevation profile's enrichment bands. Under a publishable key it is
 *   answered only where the organization's safety rules set `with_profile_analysis`.
 * - `publish`: the acts that hand an organization's work to the public through Map Publishing:
 *   publishing an itinerary or a zone, the itinerary download the studio offers its members, and
 *   the ways into an embed. Mountaya answers it to every member of an organization whose
 *   agreement includes Map Studio and Map Publishing, whatever their role, so a studio offers
 *   none of these acts where what they make would be refused. Publishing still asks for a role
 *   that writes, which the resource's own write rule answers. Withdrawing a publication is not
 *   gated on it, since an organization withdraws what it published whatever its agreement holds
 *   now, and neither is the download an embed or a share link offers its visitors, whose viewer
 *   holds no entitlement of its own.
 * - `video-export`: exporting an itinerary as a video at all.
 * - `video-organization-modes`: the modes and presets on top of that, the AI Director included.
 * - `white-label`: the Mountaya mark comes off the surfaces that carry it, the video preview
 *   and the frames the recorder composites. It covers the MARK and nothing else: tile
 *   attribution (MapTiler, OpenStreetMap, meteoblue) is a licence obligation and stays on
 *   every map under every agreement, which is why `collectAttributions` reads no entitlement.
 *
 * Distinct from {@link studioCapabilitySchema}, which names what a SURFACE is. These name what
 * a PERSON holds, which is why they are read through a function on {@link ViewerSeam} rather
 * than settled into a set when the context is built: a membership resolves from a lookup that
 * lands after the session does.
 */
export const studioEntitlementSchema = z.enum(studioEntitlements);

/** One entitlement the map studio reads, derived from {@link studioEntitlementSchema}. */
export type StudioEntitlement = z.infer<typeof studioEntitlementSchema>;

/** The refusals that say what is withheld, by the entitlement they are for. */
const withheldSentences: Partial<Record<StudioEntitlement, string>> = {
  "map-overlays":
    "the viewer does not hold the map-overlays entitlement: the overlays are not drawn for this viewer in the scope in view",
  "map-pois":
    "the viewer does not hold the map-pois entitlement: the points of interest are not drawn for this viewer",
};

/**
 * entitlementWithheld is the refusal a verb answers for a viewer who does not hold
 * `entitlement`: `upgrade-required`, with an `error` that names the entitlement the viewer seam
 * answered false for. It states the fact only and links nowhere, since an agent relays it to the
 * host's end user: a host adds its own remedy to what the agent reads through a `RecoveryAddendum`
 * of `@mountaya/studio/agent`. A gate lives in the handler rather than in a capability because an
 * agent dispatches through the bus too.
 *
 * The error names the entitlement and asserts nothing the seam does not answer. A viewer whose
 * memberships have not landed yet, one whose read was refused (a key whose organization or scope
 * does not reach the studio) or failed, one whose role withholds the feature, one whose key the
 * organization's safety rules narrow (an overlay allowlist that admits no overlay), and an
 * embed's viewer its host did not grant are all refused the same way, so no refusal names an
 * agreement: an agent that relays one says only that the feature is not held where the viewer
 * is. The refusals of `map-overlays` and `map-pois` say what is not drawn for this viewer, and no
 * more.
 *
 * @param entitlement - The entitlement {@link StudioViewer.hasEntitlement} answered false for.
 * @returns The refusal the handler answers.
 */
export const entitlementWithheld = (entitlement: StudioEntitlement): ToolFailure => ({
  code: "upgrade-required",
  error:
    withheldSentences[entitlement] ??
    `the viewer does not hold the ${entitlement} entitlement in the scope in view`,
  ok: false,
});

/**
 * StudioViewer is {@link ViewerSeam} with the studio's entitlement vocabulary bound in, plus the
 * one question the published seam deliberately cannot answer.
 *
 * `hasEntitlement` is a boolean, and a boolean is the right shape for a gate: a viewer either
 * may reach a surface or may not, and an unsettled session answers "may not": refusing a branch
 * you cannot yet justify is the safe direction to be wrong in.
 *
 * A resource CAP is the opposite. It is proactive rather than protective, since the platform's
 * own `limit-reached` is the enforcement, so being wrong before the session lands would refuse
 * work the viewer is entitled to for as long as the bootstrap takes. So a cap answers `null`
 * whenever none is in force, none applying and none known yet alike, and a gate refuses only
 * over a number. That is why it is a separate member rather than another entitlement.
 *
 * It asks for the CAP rather than for the agreement that implies it, and that is the difference
 * between a handler READING an entitlement and a handler DECIDING one. Mountaya resolves the
 * ceiling of the reader's own space (`PlatformScopeAnswer.itinerary_limit`), and what a create
 * surface needs to know is how many itineraries the scope it creates in may hold, which is a
 * number.
 *
 * {@link createPlatformViewer} answers both members from what Mountaya answers;
 * {@link createStaticViewer} and {@link createMemoryViewer} answer them for a host with no
 * session.
 */
export interface StudioViewer extends ViewerSeam<StudioEntitlement> {
  /**
   * getItineraryLimit is how many itineraries the viewer may own in the scope
   * `organizationId` names, or null when no ceiling is in force there: none applies, or the
   * session has not said yet. A gate treats both the same.
   *
   * Per scope, because a ceiling belongs to where the itineraries are kept: the reader's own
   * space may cap how many it holds while an organization's does not. Read by the create,
   * import, duplicate and suggest surfaces for the scope they create in, so a reader meets the
   * cap before filling a form rather than after submitting it.
   *
   * @param organizationId - The scope the itineraries are kept in: an organization, or the
   *   reader's own space in any spelling the host's scope predicate reads, an omitted id
   *   among them.
   */
  getItineraryLimit: (organizationId?: string | null | undefined) => number | null;
}

/**
 * inertPreferencesWrite is the `setPreferences` of a viewer that keeps no preferences: it drops
 * the write. `studio.preferences.set` reads a viewer whose write is this one as keeping nothing,
 * and answers `unavailable` under it. A host's own write is trusted to keep what it is handed,
 * whenever its `getPreferences` comes to answer it.
 */
export const inertPreferencesWrite = (_partial: ViewerPreferences): void => {};

/**
 * createStaticViewer is the viewer a host that has no session still has to supply.
 *
 * Every seam in the studio has a defined ABSENCE, and this is the viewer's. It answers no user,
 * no entitlement, no ceiling and no preferences, which is exactly what an anonymous reader is,
 * so a consumer who mounts the studio without wiring an identity gets a working read-only map
 * rather than a crash, and the gated branches refuse in the direction that is safe to be wrong
 * in. It keeps no preferences, so `studio.preferences.set` answers `unavailable` under it unless
 * `overrides` states a `setPreferences` of the host's own.
 *
 * An embed or a share link draws the overlays and the points of interest of the organization
 * that published it, whoever reads it, so its viewer states that grant through `overrides`.
 *
 * `subscribe` returns a no-op unsubscribe rather than throwing: nothing here ever changes, so
 * a listener that is never called is the honest answer.
 *
 * @param overrides - Members of the viewer the host answers itself. Defaults to none.
 * @returns A viewer that answers every question in the safe direction, but for `overrides`.
 * @example
 * ```ts
 * // The viewer of a share link, which draws the overlays of the organization that published it.
 * const viewer = createStaticViewer({
 *   hasEntitlement: (entitlement) => entitlement === "map-overlays" || entitlement === "map-pois",
 * });
 * ```
 */
export const createStaticViewer = (overrides: Partial<StudioViewer> = {}): StudioViewer => ({
  getItineraryLimit: () => null,
  getPreferences: () => null,
  getUserId: () => null,
  hasEntitlement: () => false,
  setPreferences: inertPreferencesWrite,
  subscribe: () => () => {},
  ...overrides,
});

/**
 * foldPreferences answers `held` with every member `partial` states a value for, and `held`
 * itself when that changes nothing. A member stated as undefined is not stated.
 */
const foldPreferences = (
  held: ViewerPreferences,
  partial: ViewerPreferences,
): ViewerPreferences => {
  const next: ViewerPreferences = {
    ...held,
    ...(partial.language === undefined ? {} : { language: partial.language }),
    ...(partial.length === undefined ? {} : { length: partial.length }),
    ...(partial.pace === undefined ? {} : { pace: partial.pace }),
    ...(partial.slope === undefined ? {} : { slope: partial.slope }),
    ...(partial.temperature === undefined ? {} : { temperature: partial.temperature }),
  };

  return next.language === held.language &&
    next.length === held.length &&
    next.pace === held.pace &&
    next.slope === held.slope &&
    next.temperature === held.temperature
    ? held
    : next;
};

/**
 * createMemoryViewer is the viewer for a host that has preferences to offer and nowhere to
 * put them: a demo, a docs example, a preview of somebody else's studio.
 *
 * It is {@link createStaticViewer} with the one member that WRITES made real. Preferences
 * live in a closure for the life of the mount, `setPreferences` folds a partial onto what is
 * already held and notifies, and `subscribe` returns an unsubscribe that actually removes the
 * listener. Nothing is persisted and nothing is asked of the network, so a reload starts over.
 *
 * The fold reads a partial the way the seam states it. A member the partial leaves out, or
 * states as undefined, is not stated and keeps the held value. A write that changes nothing
 * tells nobody and `getPreferences` keeps answering the same object, so a subscriber that
 * compares by identity renders for a change and for nothing else. A subscriber that throws
 * stops neither the write nor the subscribers after it (`notifyListeners`).
 *
 * It exists because the absent case here is not honest. `withPreferencesControl` defaults to
 * ON, so a host that wires no viewer renders a full units panel whose every control is
 * refused with `unavailable`: the radio does not move and the profile keeps reading
 * kilometers. A panel that cannot act is worse than no panel, and the fix belongs here rather
 * than in each host.
 *
 * Entitlements are NOT widened. `hasEntitlement` still answers false unless an override says
 * otherwise, because "I can remember your units" is not "you may reach the gated surfaces", and
 * a convenience default that granted both would be the wrong thing to reach for by accident.
 *
 * `initial` seeds what the viewer is presumed to want before touching anything, which is where
 * a host puts the language it already resolved for the rest of its page.
 *
 * @param initial - The preferences held before the first write. Defaults to none.
 * @param overrides - Members of the viewer the host answers itself, over the memory viewer's
 *   own. Defaults to none.
 */
export const createMemoryViewer = (
  initial: ViewerPreferences = {},
  overrides: Partial<StudioViewer> = {},
): StudioViewer => {
  let preferences: ViewerPreferences = { ...initial };
  const listeners = new Set<() => void>();

  return {
    ...createStaticViewer(),
    getPreferences: () => preferences,
    setPreferences: (partial) => {
      // A fold, not a replace: the seam's contract is that a surface changing the length
      // system says only that, so a partial arriving with one key must not clear the four
      // the viewer already chose. A key stated as undefined is a key not stated.
      const next = foldPreferences(preferences, partial);
      if (next === preferences) {
        return;
      }

      preferences = next;
      notifyListeners(listeners);
    },
    subscribe: (onChange) => {
      listeners.add(onChange);

      return () => {
        listeners.delete(onChange);
      };
    },
    ...overrides,
  };
};
